import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.rf-funserver.my.id').replace(/\/+$/, '');

// ── Warna ras ──────────────────────────────────────────────
const RACE_COLOR = {
  Bellato: '#4488FF',
  Accretia: '#FF6644',
  Cora: '#44CC88',
};

// Deteksi ras dari nama atau beri default
function getRace(/* bisa di-extend nanti jika API kirim data ras */) {
  return '—';
}

function numFmt(n = 0) {
  return Number(n).toLocaleString('id-ID');
}

function getAvatarBg(name = '') {
  const palette = ['#c0392b', '#8e44ad', '#1a6eb5', '#c0831a', '#16a085', '#a93226', '#d35400', '#27ae60'];
  let h = 0;
  for (const c of name) h += c.charCodeAt(0);
  return palette[h % palette.length];
}

// ── Streak Badge ────────────────────────────────────────────
function StreakBadge({ days }) {
  if (days >= 7) return (
    <span style={{ background: 'rgba(204,16,16,0.2)', border: '1px solid rgba(204,16,16,0.4)' }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold text-red-300">
      🔥 Streak {days} Hari MAX
    </span>
  );
  if (days >= 5) return (
    <span style={{ background: 'rgba(255,165,0,0.15)', border: '1px solid rgba(255,165,0,0.3)' }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold text-orange-300">
      🔥 Streak {days} Hari
    </span>
  );
  if (days >= 3) return (
    <span style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.2)' }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold text-yellow-500">
      🔥 {days} Hari
    </span>
  );
  return (
    <span style={{ background: 'rgba(80,80,80,0.3)', border: '1px solid #333' }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-gray-500">
      Hari ke-{days}
    </span>
  );
}

// ── Podium Card ─────────────────────────────────────────────
function PodiumCard({ player, rank }) {
  const configs = {
    1: {
      border: '#FFD700', shadow: 'rgba(255,215,0,0.25)',
      bg: 'linear-gradient(160deg,#1A1200,#2A1F00,#1A1200)',
      num: '#FFD700', numGlow: '0 0 20px #FFD700',
      avatar: 'linear-gradient(135deg,#B8860B,#FFD700)',
    },
    2: {
      border: '#C0C0C0', shadow: 'rgba(192,192,192,0.12)',
      bg: 'linear-gradient(160deg,#111,#1C1C1C,#111)',
      num: '#C0C0C0', numGlow: 'none',
      avatar: 'linear-gradient(135deg,#666,#C0C0C0)',
    },
    3: {
      border: '#CD7F32', shadow: 'rgba(205,127,50,0.12)',
      bg: 'linear-gradient(160deg,#110A00,#1C1000,#110A00)',
      num: '#CD7F32', numGlow: 'none',
      avatar: 'linear-gradient(135deg,#7a4800,#CD7F32)',
    },
  };
  const cfg = configs[rank];
  if (!player) return (
    <div className="flex-1 max-w-xs" style={{ maxWidth: 280 }}>
      <div className="rounded-xl p-6 text-center" style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, opacity: 0.4 }}>
        <div className="text-2xl font-black" style={{ color: cfg.num }}>#{rank}</div>
        <div className="text-gray-600 mt-4 text-sm">Belum ada data</div>
      </div>
    </div>
  );

  return (
    <div className="flex-1 text-center transition-transform hover:-translate-y-1 cursor-default"
      style={{ maxWidth: 280 }}>
      <div className="relative rounded-xl p-5 pt-7"
        style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, boxShadow: `0 0 30px ${cfg.shadow}` }}>

        {rank === 1 && (
          <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-3xl"
            style={{ filter: 'drop-shadow(0 0 8px rgba(255,215,0,0.8))', animation: 'bounce 2s infinite' }}>
            👑
          </div>
        )}

        <div className="font-black text-2xl mb-3" style={{ color: cfg.num, textShadow: cfg.numGlow, fontFamily: 'monospace' }}>
          #{rank}
        </div>

        <div className="w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center text-2xl font-bold text-black"
          style={{ background: cfg.avatar, boxShadow: rank === 1 ? '0 0 20px rgba(255,215,0,0.4)' : 'none' }}>
          {player.name.charAt(0).toUpperCase()}
        </div>

        <div className="font-bold text-white text-base leading-tight">{player.name}</div>
        <div className="text-xs text-gray-500 mb-3">Level {player.level}</div>

        <div className="font-black text-xl mb-1" style={{ color: cfg.num, fontFamily: 'monospace' }}>
          {numFmt(player.coinEarned)}
        </div>
        <div className="text-xs uppercase tracking-widest text-gray-600 mb-3">CashCoin</div>

        <StreakBadge days={player.streakDays} />
        {player.hasPeakHour && (
          <div className="mt-2">
            <span style={{ background: 'rgba(255,30,30,0.15)', border: '1px solid rgba(255,30,30,0.3)' }}
              className="text-xs px-2 py-0.5 rounded text-red-400 font-semibold">
              ⚡ Peak Hour
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Stat Card ───────────────────────────────────────────────
function StatCard({ icon, value, label, accent }) {
  const colors = {
    gold: { top: 'linear-gradient(90deg,transparent,#FFD700,transparent)', val: '#FFD700' },
    red: { top: 'linear-gradient(90deg,transparent,#FF3333,transparent)', val: '#FF4444' },
    white: { top: 'linear-gradient(90deg,transparent,#555,transparent)', val: '#F0F0F0' },
    green: { top: 'linear-gradient(90deg,transparent,#22cc22,transparent)', val: '#22cc22' },
  };
  const c = colors[accent] || colors.white;
  return (
    <div className="relative rounded-xl p-5 overflow-hidden transition-all"
      style={{ background: '#1A1A1A', border: '1px solid #252525' }}>
      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: c.top }} />
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-2xl font-black leading-none mb-1" style={{ color: c.val, fontFamily: 'monospace' }}>
        {value}
      </div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────
export default function Leaderboard() {
  const [tab, setTab] = useState('today');
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({ totalCoin: 0, activeAccounts: 0, streakMaxCount: 0 });
  const [isPeak, setIsPeak] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (t) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/leaderboard?tab=${t}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Server error');
      setData(json.leaderboard || []);
      setStats(json.stats || {});
      setIsPeak(json.isPeakHour || false);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(tab); }, [tab, fetchData]);

  const top3 = data.slice(0, 3);
  const rest = data.slice(3);

  const tabLabel = { today: 'Hari Ini', week: 'Minggu Ini', alltime: 'All Time' };

  return (
    <div className="min-h-screen pt-20 pb-16 px-4"
      style={{ background: '#0A0A0A', color: '#F0F0F0' }}>

      {/* ─── HEADER ─── */}
      <div className="text-center py-8 max-w-4xl mx-auto">
        <p className="text-sm font-bold tracking-widest uppercase mb-1"
          style={{ color: '#FF2222', textShadow: '0 0 15px rgba(255,34,34,0.5)', fontFamily: 'monospace' }}>
          ⚔ RF FUN REBORN ⚔
        </p>
        <h1 className="text-4xl md:text-6xl font-black leading-none mb-2"
          style={{ background: 'linear-gradient(135deg,#FFF0A0,#FFD700,#B8860B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: 'monospace' }}>
          CASHCOIN
        </h1>
        <h2 className="text-3xl md:text-5xl font-black"
          style={{ background: 'linear-gradient(135deg,#FFF0A0,#FFD700,#B8860B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: 'monospace' }}>
          LEADERBOARD
        </h2>
        <p className="text-sm text-gray-500 mt-2 tracking-widest">TOP EARNERS — ONLINE REWARD SYSTEM</p>
        <div className="w-28 h-0.5 mx-auto mt-4"
          style={{ background: 'linear-gradient(90deg,transparent,#FFD700,transparent)' }} />
      </div>

      <div className="max-w-5xl mx-auto">

        {/* ─── TAB ─── */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {[['today', '🔥 Hari Ini'], ['week', '📅 Minggu Ini'], ['alltime', '👑 All Time']].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className="px-5 py-2 rounded-full text-sm font-bold tracking-wide transition-all"
              style={tab === k
                ? { background: 'linear-gradient(135deg,#8B0000,#CC1010)', border: '1px solid #FF2222', color: '#FFD700', boxShadow: '0 0 16px rgba(204,16,16,0.5)' }
                : { background: '#1A1A1A', border: '1px solid #333', color: '#666' }}>
              {label}
            </button>
          ))}
        </div>

        {/* ─── STATS ─── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatCard icon="💰" value={numFmt(stats.totalCoin)} label={`Total Coin ${tabLabel[tab]}`} accent="gold" />
          <StatCard icon="👥" value={stats.activeAccounts} label="Akun Aktif" accent="red" />
          <StatCard icon="🔥" value={stats.streakMaxCount} label="Player Streak 7 Hari" accent="white" />
          <StatCard
            icon="⚡"
            value={isPeak ? 'AKTIF ✓' : 'OFF'}
            label="Peak Hour (19:00–22:00)"
            accent={isPeak ? 'green' : 'white'}
          />
        </div>

        {/* ─── LOADING / ERROR ─── */}
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block w-10 h-10 rounded-full border-2 border-yellow-500 border-t-transparent animate-spin mb-3" />
            <p className="text-gray-500 text-sm">Mengambil data dari database...</p>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-20 max-w-md mx-auto">
            <div className="text-4xl mb-3">⚠️</div>
            <p className="text-red-400 font-semibold mb-1">Gagal memuat data</p>
            <p className="text-gray-600 text-sm mb-4">{error}</p>
            <button onClick={() => fetchData(tab)}
              className="px-4 py-2 rounded-lg text-sm font-semibold"
              style={{ background: 'rgba(204,16,16,0.2)', border: '1px solid rgba(204,16,16,0.5)', color: '#ff8080' }}>
              Coba Lagi
            </button>
          </div>
        )}

        {!loading && !error && data.length === 0 && (
          <div className="text-center py-20 text-gray-600">
            <div className="text-4xl mb-3">📊</div>
            <p>Belum ada data untuk periode ini.</p>
            <p className="text-sm mt-1">Sistem akan otomatis mengisi saat player mendapat reward.</p>
          </div>
        )}

        {!loading && !error && data.length > 0 && (
          <>
            {/* ─── PODIUM TOP 3 ─── */}
            <div className="flex items-end justify-center gap-3 mb-10 flex-wrap md:flex-nowrap">
              {/* order: rank 2, rank 1, rank 3 */}
              <PodiumCard player={top3[1]} rank={2} />
              <PodiumCard player={top3[0]} rank={1} />
              <PodiumCard player={top3[2]} rank={3} />
            </div>

            {/* ─── TABLE ─── */}
            {rest.length > 0 && (
              <div className="rounded-2xl overflow-hidden" style={{ background: '#111', border: '1px solid #2A2A2A' }}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
                  <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#FFD700' }}>
                    📊 Ranking Lengkap
                  </span>
                  <span className="text-xs text-gray-600 flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    Update tiap 10 menit
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="px-4 py-3 text-center text-xs uppercase tracking-widest text-gray-600 w-12">#</th>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-gray-600">Player</th>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-gray-600 hidden sm:table-cell">Streak</th>
                        <th className="px-4 py-3 text-right text-xs uppercase tracking-widest text-gray-600">Coin</th>
                        <th className="px-4 py-3 text-right text-xs uppercase tracking-widest text-gray-600 hidden md:table-cell">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rest.map((p) => (
                        <tr key={p.accountSerial}
                          className="border-b border-gray-900 transition-colors hover:bg-yellow-900/5">
                          <td className="px-4 py-3 text-center font-bold text-gray-500 font-mono">
                            #{p.rank}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-sm text-white shrink-0"
                                style={{ background: getAvatarBg(p.name) }}>
                                {p.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-semibold text-white">{p.name}</div>
                                <div className="text-xs text-gray-500">Lv.{p.level}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <StreakBadge days={p.streakDays} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="font-bold font-mono" style={{ color: '#FFD700' }}>
                              {numFmt(p.coinEarned)}
                            </div>
                            {p.hasPeakHour && (
                              <div className="mt-0.5">
                                <span style={{ background: 'rgba(255,30,30,0.1)', border: '1px solid rgba(255,30,30,0.3)' }}
                                  className="text-xs px-1.5 py-0.5 rounded text-red-400">⚡ Peak</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 font-mono text-xs hidden md:table-cell">
                            {numFmt(p.coinEarned)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ─── FOOTER NOTE ─── */}
        <p className="text-center text-xs text-gray-700 mt-10 leading-relaxed">
          <span style={{ color: '#B8860B' }}>RF FUN REBORN</span> — Online Reward System<br />
          Login tiap hari &amp; online jam 19:00–22:00 untuk reward maksimal!
        </p>
      </div>
    </div>
  );
}
